import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquare,
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  RefreshCw,
  Eye,
  Save,
  X,
  ChevronDown,
  ChevronUp,
  BarChart3,
  TrendingUp,
  FileText,
  Languages,
  Sparkles,
  Users,
  Activity,
  Target,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { db, appId } from '../firebase/firebaseConfig.jsx';
import {
  collection,
  query,
  orderBy,
  getDocs,
  getDoc,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  where,
  getCountFromServer,
  serverTimestamp
} from 'firebase/firestore';
import { useToastMessage } from '../contexts/ToastMessageContext.jsx';
import { apiClient } from '../config/apiConfig';
import { testFAQAccess } from '../utils/testFAQAccess';

const CATEGORIES = [
  { id: 'labor', label: '노무', color: 'blue' },
  { id: 'legal', label: '법률', color: 'purple' },
  { id: 'medical', label: '의료', color: 'red' },
  { id: 'life', label: '생활', color: 'green' },
  { id: 'other', label: '기타', color: 'gray' }
];

const LANGUAGES = [
  { id: 'ko', label: '한국어', flag: '🇰🇷' },
  { id: 'my', label: 'မြန်မာ', flag: '🇲🇲' }
  // 영어 제외
];

const ITEMS_PER_PAGE = 10;

const ConsultationFAQManagementPage = () => {
  const { showToastMessage } = useToastMessage();

  // State management
  const [loading, setLoading] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [filteredFaqs, setFilteredFaqs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLanguage, setSelectedLanguage] = useState('all');
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [expandedFaqs, setExpandedFaqs] = useState(new Set());
  
  // Form states (영어 제외)
  const [formData, setFormData] = useState({
    question: { ko: '', my: '' },
    answer: { ko: '', my: '' },
    category: 'labor',
    keywords: [],
    priority: 5,
    tags: []
  });

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    byCategory: {},
    byLanguage: {},
    topUsed: []
  });

  // User Query Statistics
  const [queryStats, setQueryStats] = useState({
    total: 0,
    byCategory: {},
    byLanguage: {},
    byDay: {},
    faqMatchRate: 0,
    unansweredCount: 0,
    answeredCount: 0,
    topQuestions: []
  });
  const [loadingQueryStats, setLoadingQueryStats] = useState(false);
  const [statsPeriod, setStatsPeriod] = useState('30');

  // Answer Generation States
  const [generatingAnswer, setGeneratingAnswer] = useState(null); // { faqId, language }
  
  // Question Completion States
  const [completingQuestion, setCompletingQuestion] = useState(null); // { faqId }
  
  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const preservePageRef = useRef(false); // 페이지 유지 플래그

  // Load FAQs
  useEffect(() => {
    console.log('🔍 ConsultationFAQManagementPage 마운트됨');
    console.log('📋 초기 상태:', {
      appId,
      db: db ? 'exists' : 'null',
      CATEGORIES: CATEGORIES.map(c => c.id)
    });
    
    // FAQ 접근 테스트 실행
    testFAQAccess().catch(err => {
      console.error('FAQ 접근 테스트 실패:', err);
    });
    
    loadFAQs();
    loadStatistics();
    loadQueryStatistics();
  }, [statsPeriod]);

  // Filter FAQs
  useEffect(() => {
    filterFAQs();
  }, [faqs, searchTerm, selectedCategory, selectedLanguage]);

  // 개별 FAQ 업데이트 (전체 리로드 없이)
  const updateSingleFAQ = async (faqId, category) => {
    try {
      // 페이지 유지 플래그 설정 (filterFAQs에서 페이지를 리셋하지 않도록)
      preservePageRef.current = true;
      
      const faqRef = doc(db, `artifacts/${appId}/consultation_faqs/${category}/faqs/${faqId}`);
      const faqSnap = await getDoc(faqRef);
      
      if (!faqSnap.exists()) {
        console.warn('업데이트할 FAQ를 찾을 수 없습니다:', faqId);
        return;
      }

      const updatedFaqData = {
        id: faqSnap.id,
        category: category,
        ...faqSnap.data()
      };

      // 현재 FAQ 목록에서 해당 FAQ 찾아서 업데이트
      setFaqs(prevFaqs => {
        return prevFaqs.map(faq => {
          // 그룹핑된 FAQ의 경우 originalIds 확인
          if (faq.id === faqId || (faq.originalIds && faq.originalIds.includes(faqId))) {
            // 그룹핑된 FAQ인 경우 업데이트
            if (faq.originalIds && faq.originalIds.length > 0) {
              const updatedQuestion = { ...faq.question };
              const updatedAnswer = { ...faq.answer };
              
              // 업데이트된 데이터 반영
              if (updatedFaqData.question) {
                Object.assign(updatedQuestion, updatedFaqData.question);
              }
              if (updatedFaqData.answer) {
                Object.assign(updatedAnswer, updatedFaqData.answer);
              }

              return {
                ...faq,
                question: updatedQuestion,
                answer: updatedAnswer,
                hasAnswer: updatedFaqData.hasAnswer || faq.hasAnswer,
                version: updatedFaqData.version || faq.version,
                updatedAt: updatedFaqData.updatedAt || faq.updatedAt
              };
            } else {
              // 단일 FAQ인 경우 직접 교체
              return updatedFaqData;
            }
          }
          return faq;
        });
      });

      console.log('✅ 개별 FAQ 업데이트 완료:', faqId);
      
      // 플래그 리셋 (다음 필터링 시 정상 동작하도록)
      setTimeout(() => {
        preservePageRef.current = false;
      }, 100);
    } catch (error) {
      console.error('❌ 개별 FAQ 업데이트 실패:', error);
      preservePageRef.current = false;
      // 실패 시 전체 리로드로 폴백
      await loadFAQs(true);
    }
  };

  const loadFAQs = async (preservePage = false) => {
    // 페이지 유지 플래그 설정
    preservePageRef.current = preservePage;
    
    setLoading(true);
    try {
      const allFaqs = [];
      
      console.log('🔍 FAQ 로드 시작, appId:', appId);
      
      for (const category of CATEGORIES) {
        try {
          // Firebase Client SDK 서브컬렉션 접근
          // 실제 구조: artifacts/{appId}/consultation_faqs/{category}/faqs/{faqId}
          console.log(`📋 ${category.id} 카테고리 로드 중...`);
          console.log(`   appId: ${appId}`);
          
          // 부모 문서 참조 생성 후 서브컬렉션 접근
          const categoryDocPath = `artifacts/${appId}/consultation_faqs/${category.id}`;
          console.log(`   부모 문서 경로: ${categoryDocPath}`);
          
          const categoryDocRef = doc(db, categoryDocPath);
          const faqRef = collection(categoryDocRef, 'faqs');
          
          console.log(`   서브컬렉션 접근 시도...`);
          
          const snapshot = await getDocs(faqRef);
          
          console.log(`   ✅ ${category.id}: ${snapshot.size}개 FAQ 발견`);
          
          snapshot.forEach(docSnap => {
            const data = docSnap.data();
            const questionText = data.question?.ko || data.question?.my || data.question?.en || '질문 없음';
            console.log(`   - FAQ ID: ${docSnap.id}`);
            console.log(`     질문 미리보기: ${questionText.substring(0, 50)}...`);
            
            allFaqs.push({
              id: docSnap.id,
              category: category.id,
              ...data
            });
          });
        } catch (categoryError) {
          console.error(`❌ ${category.id} 카테고리 로드 실패:`, categoryError);
          console.error('   에러 상세:', {
            message: categoryError.message,
            code: categoryError.code,
            name: categoryError.name,
            stack: categoryError.stack?.substring(0, 500)
          });
          
          // 에러 상세 정보를 토스트 메시지에 표시
          const errorMsg = categoryError.code 
            ? `${category.label} 카테고리 로드 실패 (${categoryError.code}): ${categoryError.message}`
            : `${category.label} 카테고리 로드 실패: ${categoryError.message}`;
          showToastMessage(errorMsg, 'error');
        }
      }

      console.log(`📊 총 ${allFaqs.length}개 FAQ 로드 완료 (그룹핑 전)`);

      if (allFaqs.length === 0) {
        console.warn('⚠️ FAQ가 하나도 로드되지 않았습니다!');
        console.warn('   확인 사항:');
        console.warn('   1. appId:', appId);
        console.warn('   2. Firebase 연결 상태');
        console.warn('   3. Firestore 보안 규칙 확인 필요');
        showToastMessage('FAQ를 불러올 수 없습니다. 콘솔 로그를 확인해주세요.', 'warning');
      } else {
        console.log('✅ FAQ 데이터 샘플 (최대 3개):');
        allFaqs.slice(0, 3).forEach((faq, idx) => {
          console.log(`   ${idx + 1}. ID: ${faq.id}, 카테고리: ${faq.category}`);
          console.log(`      질문(ko): ${(faq.question?.ko || '').substring(0, 50)}...`);
        });
      }

      // FAQ 그룹핑: 한국어+미얀마어 조합 기준으로 중복 제거 (먼저 그룹핑)
      const groupedFaqs = groupFAQsByQuestion(allFaqs);
      
      // 우선순위 및 사용 빈도 순으로 정렬
      groupedFaqs.sort((a, b) => {
        const scoreA = (a.priority || 0) * 10 + (a.usageCount || 0);
        const scoreB = (b.priority || 0) * 10 + (b.usageCount || 0);
        return scoreB - scoreA;
      });
      
      setFaqs(groupedFaqs);
      
      // 페이지 초기화 (preservePage가 true면 유지)
      if (!preservePage) {
        setCurrentPage(1);
      }
      console.log(`✅ FAQ 상태 업데이트 완료 (그룹핑 후: ${groupedFaqs.length}개)`);
      console.log('🔍 FAQ 상태 확인:', {
        faqsLength: allFaqs.length,
        filteredFaqsLength: filteredFaqs.length
      });
    } catch (error) {
      console.error('❌ FAQ 로드 실패:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        appId,
        db: db ? 'db exists' : 'db is null'
      });
      showToastMessage(`FAQ를 불러오는데 실패했습니다: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const statsData = {
        total: 0,
        byCategory: {},
        byLanguage: {},
        topUsed: []
      };

      for (const category of CATEGORIES) {
        try {
          // Firebase Client SDK: 전체 경로를 문자열로 직접 접근
          const collectionPath = `artifacts/${appId}/consultation_faqs/${category.id}/faqs`;
          
          let snapshot;
          try {
            const faqRef = collection(db, collectionPath);
            snapshot = await getDocs(faqRef);
          } catch (error1) {
            // 방법 1 실패 시 방법 2 시도
            const categoryDocRef = doc(db, `artifacts/${appId}/consultation_faqs/${category.id}`);
            const faqRef = collection(categoryDocRef, 'faqs');
            snapshot = await getDocs(faqRef);
          }
          
          // FAQ 그룹핑하여 개수 계산 (한국어+미얀마어 조합 기준, 영어 제외)
          const categoryFaqs = [];
          snapshot.forEach(docSnap => {
            const data = docSnap.data();
            categoryFaqs.push({
              id: docSnap.id,
              category: category.id,
              question: {
                ko: data.question?.ko || '',
                my: data.question?.my || ''
                // 영어 제외
              },
              ...data
            });
          });
          
          const groupedCategoryFaqs = groupFAQsByQuestion(categoryFaqs);
          const categoryCount = groupedCategoryFaqs.length;
          
          statsData.byCategory[category.id] = categoryCount;
          statsData.total += categoryCount;

          snapshot.forEach(docSnap => {
            const data = docSnap.data();
            const usageCount = data.usageCount || 0;
            
            if (usageCount > 0) {
              statsData.topUsed.push({
                id: docSnap.id,
                category: category.id,
                question: data.question,
                usageCount
              });
            }

            // 언어별 통계
            if (data.answer) {
              Object.keys(data.answer).forEach(lang => {
                statsData.byLanguage[lang] = (statsData.byLanguage[lang] || 0) + 1;
              });
            }
          });
        } catch (error) {
          console.error(`${category.id} 통계 로드 실패:`, error);
          statsData.byCategory[category.id] = 0;
        }
      }

      statsData.topUsed.sort((a, b) => b.usageCount - a.usageCount);
      statsData.topUsed = statsData.topUsed.slice(0, 10);
      
      setStats(statsData);
    } catch (error) {
      console.error('통계 로드 실패:', error);
    }
  };

  // FAQ 그룹핑 함수: 한국어+미얀마어 조합 기준으로 같은 질문을 하나로 그룹핑
  const groupFAQsByQuestion = (faqs) => {
    const groupedMap = new Map();
    
    faqs.forEach(faq => {
      // 한국어와 미얀마어 질문 조합을 키로 사용
      const koQuestion = (faq.question?.ko || '').trim();
      const myQuestion = (faq.question?.my || '').trim();
      const groupKey = `${faq.category}|${koQuestion}|${myQuestion}`;
      
      if (!groupedMap.has(groupKey)) {
        // 첫 번째 FAQ를 기준으로 그룹 생성
        groupedMap.set(groupKey, {
          ...faq,
          // 여러 FAQ의 사용 횟수 합산
          usageCount: faq.usageCount || 0,
          // 원본 FAQ ID 목록 (필요시)
          originalIds: [faq.id]
        });
      } else {
        // 기존 그룹에 병합: 사용 횟수 합산
        const existing = groupedMap.get(groupKey);
        existing.usageCount = (existing.usageCount || 0) + (faq.usageCount || 0);
        existing.originalIds.push(faq.id);
        
        // 답변이 있는 경우 우선 적용 (우선순위: 한국어 > 미얀마어)
        if (!existing.answer?.ko && faq.answer?.ko) {
          existing.answer = { ...existing.answer, ko: faq.answer.ko };
        }
        if (!existing.answer?.my && faq.answer?.my) {
          existing.answer = { ...existing.answer, my: faq.answer.my };
        }
      }
    });
    
    return Array.from(groupedMap.values());
  };

  const filterFAQs = () => {
    console.log('🔍 filterFAQs 실행:', {
      totalFaqs: faqs.length,
      searchTerm,
      selectedCategory,
      selectedLanguage
    });
    let filtered = [...faqs];

    // 카테고리 필터
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(faq => faq.category === selectedCategory);
    }

    // 언어 필터 (영어 제외)
    if (selectedLanguage !== 'all') {
      filtered = filtered.filter(faq => {
        // 영어는 필터링 대상에서 제외
        if (selectedLanguage === 'en') return false;
        
        const question = faq.question?.[selectedLanguage] || '';
        const answer = faq.answer?.[selectedLanguage] || '';
        return question.length > 0 || answer.length > 0;
      });
    }

    // 검색 필터
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(faq => {
        const questions = Object.values(faq.question || {}).join(' ').toLowerCase();
        const answers = Object.values(faq.answer || {}).join(' ').toLowerCase();
        const keywords = (faq.keywords || []).join(' ').toLowerCase();
        return questions.includes(term) || answers.includes(term) || keywords.includes(term);
      });
    }

    console.log(`   필터링 결과: ${filtered.length}개 (전체: ${faqs.length}개)`);
    
    // 페이지 초기화 (preservePageRef가 true면 유지)
    if (!preservePageRef.current) {
      setCurrentPage(1);
    } else {
      // 페이지 유지 플래그 리셋
      preservePageRef.current = false;
    }
    
    setFilteredFaqs(filtered);
  };
  
  // 페이지네이션 계산
  const totalPages = Math.ceil(filteredFaqs.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedFaqs = filteredFaqs.slice(startIndex, endIndex);

  const handleAddFAQ = () => {
    setIsEditMode(false);
    setEditingFaq(null);
    setFormData({
      question: { ko: '', my: '' },
      answer: { ko: '', my: '' },
      category: 'labor',
      keywords: [],
      priority: 5,
      tags: []
    });
    setIsModalOpen(true);
  };

  const handleEditFAQ = (faq) => {
    setIsEditMode(true);
    setEditingFaq(faq);
    setFormData({
      question: { 
        ko: faq.question?.ko || '', 
        my: faq.question?.my || ''
        // 영어 제외
      },
      answer: { 
        ko: faq.answer?.ko || '', 
        my: faq.answer?.my || ''
        // 영어 제외
      },
      category: faq.category || 'labor',
      keywords: faq.keywords || [],
      priority: faq.priority || 5,
      tags: faq.tags || []
    });
    setIsModalOpen(true);
  };

  const handleSaveFAQ = async () => {
    if (!formData.question.ko.trim() && !formData.question.my.trim()) {
      showToastMessage('최소한 한 가지 언어의 질문을 입력해주세요.', 'error');
      return;
    }

    setLoading(true);
    try {
      // 영어 필드 제거
      const cleanQuestion = { ko: formData.question.ko, my: formData.question.my };
      const cleanAnswer = { ko: formData.answer.ko, my: formData.answer.my };
      
      const faqData = {
        ...formData,
        question: cleanQuestion,
        answer: cleanAnswer,
        updatedAt: serverTimestamp(), // Firestore 서버 타임스탬프 사용
        generatedBy: isEditMode ? (editingFaq?.generatedBy || 'manual') : 'manual',
        version: isEditMode ? ((editingFaq?.version || 1) + 1) : 1
      };

      if (!isEditMode) {
        faqData.createdAt = serverTimestamp(); // Firestore 서버 타임스탬프 사용
        faqData.usageCount = 0;
      }

      // FAQ ID 결정: 수정 모드일 때는 그룹핑된 FAQ의 경우 첫 번째 originalIds 사용
      let faqId;
      if (isEditMode && editingFaq) {
        // 그룹핑된 FAQ의 경우 첫 번째 originalIds 사용, 아니면 editingFaq.id 사용
        faqId = (editingFaq.originalIds && editingFaq.originalIds.length > 0) 
          ? editingFaq.originalIds[0] 
          : editingFaq.id;
      } else {
        // 새 FAQ 생성 시 고유 ID 생성
        faqId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      }
      
      const faqRef = doc(
        db,
        `artifacts/${appId}/consultation_faqs/${formData.category}/faqs/${faqId}`
      );

      if (isEditMode && editingFaq) {
        // 수정 시: 기존 문서 업데이트
        await updateDoc(faqRef, faqData);
        showToastMessage('FAQ가 수정되었습니다.', 'success');
      } else {
        // 추가 시: 새 문서 생성
        await setDoc(faqRef, faqData);
        showToastMessage('FAQ가 추가되었습니다.', 'success');
      }

      setIsModalOpen(false);
      await loadFAQs();
      await loadStatistics();
    } catch (error) {
      console.error('FAQ 저장 실패:', error);
      showToastMessage('FAQ 저장에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFAQ = async (faq) => {
    // 그룹핑된 FAQ의 경우 모든 originalIds 삭제 여부 확인
    const hasMultipleIds = faq.originalIds && faq.originalIds.length > 1;
    const confirmMessage = hasMultipleIds
      ? `이 FAQ는 ${faq.originalIds.length}개의 중복 항목으로 구성되어 있습니다. 모든 항목을 삭제하시겠습니까?`
      : '이 FAQ를 삭제하시겠습니까?';
    
    if (!window.confirm(confirmMessage)) return;

    setLoading(true);
    try {
      // 그룹핑된 FAQ의 경우 모든 originalIds 삭제
      const idsToDelete = (faq.originalIds && faq.originalIds.length > 0) 
        ? faq.originalIds 
        : [faq.id];
      
      let deletedCount = 0;
      let failedCount = 0;
      
      for (const idToDelete of idsToDelete) {
        try {
          const faqRef = doc(db, `artifacts/${appId}/consultation_faqs/${faq.category}/faqs/${idToDelete}`);
          await deleteDoc(faqRef);
          deletedCount++;
        } catch (error) {
          console.error(`FAQ 삭제 실패 (ID: ${idToDelete}):`, error);
          failedCount++;
        }
      }
      
      if (failedCount === 0) {
        showToastMessage(
          hasMultipleIds 
            ? `${deletedCount}개의 FAQ가 삭제되었습니다.` 
            : 'FAQ가 삭제되었습니다.', 
          'success'
        );
      } else {
        showToastMessage(
          `${deletedCount}개 삭제 완료, ${failedCount}개 삭제 실패`, 
          'warning'
        );
      }
      
      await loadFAQs();
      await loadStatistics();
    } catch (error) {
      console.error('FAQ 삭제 실패:', error);
      showToastMessage('FAQ 삭제에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (faqId) => {
    const newExpanded = new Set(expandedFaqs);
    if (newExpanded.has(faqId)) {
      newExpanded.delete(faqId);
    } else {
      newExpanded.add(faqId);
    }
    setExpandedFaqs(newExpanded);
  };

  const getCategoryColor = (categoryId) => {
    const category = CATEGORIES.find(c => c.id === categoryId);
    return category?.color || 'gray';
  };

  // 사용자 질의 통계 로드
  const loadQueryStatistics = async () => {
    setLoadingQueryStats(true);
    try {
      const response = await apiClient.get(`/api/consultation/query-stats?period=${statsPeriod}`);
      if (response.data.success) {
        setQueryStats(response.data.data);
      }
    } catch (error) {
      console.error('질의 통계 로드 실패:', error);
      showToastMessage('질의 통계를 불러오는데 실패했습니다.', 'error');
    } finally {
      setLoadingQueryStats(false);
    }
  };

  // 답변 생성 (특정 언어)
  const handleGenerateAnswer = async (faq, language) => {
    const question = faq.question?.[language];
    if (!question || !question.trim()) {
      showToastMessage(`${LANGUAGES.find(l => l.id === language)?.label} 질문이 없습니다.`, 'error');
      return;
    }

    // 그룹핑된 FAQ의 경우 첫 번째 originalIds 사용
    const faqId = (faq.originalIds && faq.originalIds.length > 0) ? faq.originalIds[0] : faq.id;
    
    console.log('🚀 답변 생성 시작:', {
      faqId,
      language,
      category: faq.category,
      questionPreview: question.substring(0, 50) + '...',
      originalIds: faq.originalIds
    });

    setGeneratingAnswer({ faqId: faq.id, language });
    try {
      console.log('📤 API 요청 전송:', {
        url: '/api/consultation/generate-answer',
        question: question.substring(0, 50) + '...',
        questionLength: question.length,
        category: faq.category,
        language,
        timestamp: new Date().toISOString()
      });

      // 타임아웃을 늘린 별도 요청 설정 (답변 생성은 시간이 오래 걸릴 수 있음)
      const response = await apiClient.post('/api/consultation/generate-answer', {
        question,
        category: faq.category,
        language
      }, {
        timeout: 120000 // 2분
      });

      console.log('📥 API 응답 수신:', {
        success: response.data.success,
        hasAnswer: !!response.data.answer,
        answerLength: response.data.answer?.length || 0,
        answerPreview: response.data.answer?.substring(0, 100) || 'N/A',
        category: response.data.category,
        language: response.data.language,
        status: response.status,
        fullResponse: response.data
      });

      if (response.data.success && response.data.answer) {
        // FAQ 업데이트 (실제 구조: artifacts/{appId}/consultation_faqs/{category}/faqs/{faqId})
        console.log('💾 FAQ 업데이트 시작:', {
          faqId,
          category: faq.category,
          language
        });

        const faqRef = doc(db, `artifacts/${appId}/consultation_faqs/${faq.category}/faqs/${faqId}`);
        const currentAnswers = { ...faq.answer };
        currentAnswers[language] = response.data.answer;
        
        console.log('💾 업데이트할 데이터:', {
          faqId,
          language,
          answerLength: currentAnswers[language]?.length || 0,
          allAnswerKeys: Object.keys(currentAnswers),
          currentAnswers: currentAnswers
        });

        await updateDoc(faqRef, {
          answer: currentAnswers,
          hasAnswer: true, // 답변이 생성되었음을 표시
          updatedAt: serverTimestamp(), // Firestore 서버 타임스탬프 사용
          version: (faq.version || 1) + 1
        });

        console.log('✅ FAQ 업데이트 완료 - Firestore 반영됨');
        
        // 업데이트 확인을 위한 짧은 딜레이
        await new Promise(resolve => setTimeout(resolve, 500));
        showToastMessage(`${LANGUAGES.find(l => l.id === language)?.label} 답변이 생성되었습니다.`, 'success');
        // 해당 FAQ만 업데이트 (전체 리로드 없이)
        await updateSingleFAQ(faqId, faq.category);
      } else {
        console.error('❌ API 응답 실패:', {
          success: response.data.success,
          message: response.data.message,
          answer: response.data.answer
        });
        showToastMessage(response.data.message || '답변 생성에 실패했습니다.', 'error');
      }
    } catch (error) {
      console.error('❌ 답변 생성 실패:', {
        error: error,
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      
      // 상세한 에러 메시지 표시
      let errorMessage = '답변 생성 중 오류가 발생했습니다.';
      if (error.response) {
        // 서버 응답 에러
        errorMessage = error.response.data?.message || `서버 오류: ${error.response.status} ${error.response.statusText}`;
      } else if (error.request) {
        // 요청은 보냈지만 응답을 받지 못함
        errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
      } else {
        // 요청 설정 중 오류
        errorMessage = error.message || '알 수 없는 오류가 발생했습니다.';
      }
      
      showToastMessage(errorMessage, 'error');
    } finally {
      setGeneratingAnswer(null);
    }
  };

  // 답변 재생성 (답변이 있는 경우)
  const handleRegenerateAnswer = async (faq, language) => {
    if (!window.confirm(`${LANGUAGES.find(l => l.id === language)?.label} 답변을 재생성하시겠습니까?\n기존 답변이 삭제된 후 새로 생성됩니다.`)) {
      return;
    }

    const question = faq.question?.[language];
    if (!question || !question.trim()) {
      showToastMessage(`${LANGUAGES.find(l => l.id === language)?.label} 질문이 없습니다.`, 'error');
      return;
    }

    // 그룹핑된 FAQ의 경우 첫 번째 originalIds 사용
    const faqId = (faq.originalIds && faq.originalIds.length > 0) ? faq.originalIds[0] : faq.id;
    
    console.log('🔄 답변 재생성 시작:', {
      faqId,
      language,
      category: faq.category,
      questionPreview: question.substring(0, 50) + '...',
      currentAnswerExists: !!faq.answer?.[language],
      currentAnswerLength: faq.answer?.[language]?.length || 0
    });

    setGeneratingAnswer({ faqId: faq.id, language });
    
    try {
      // 1단계: 기존 답변 삭제
      const faqRef = doc(db, `artifacts/${appId}/consultation_faqs/${faq.category}/faqs/${faqId}`);
      const currentAnswers = { ...(faq.answer || {}) };
      
      // 해당 언어 답변 삭제
      delete currentAnswers[language];
      
      console.log('🗑️ 기존 답변 삭제:', {
        faqId,
        language,
        deletedAnswerLength: faq.answer?.[language]?.length || 0,
        remainingAnswers: Object.keys(currentAnswers)
      });

      // 기존 답변 삭제 후 업데이트
      await updateDoc(faqRef, {
        answer: currentAnswers,
        updatedAt: serverTimestamp()
      });

      console.log('✅ 기존 답변 삭제 완료');

      // 2단계: 새 답변 생성
      console.log('📤 재생성 API 요청 전송:', {
        question: question.substring(0, 50) + '...',
        questionLength: question.length,
        category: faq.category,
        language,
        timestamp: new Date().toISOString()
      });

      const response = await apiClient.post('/api/consultation/generate-answer', {
        question,
        category: faq.category,
        language
      }, {
        timeout: 120000 // 2분
      });

      console.log('📥 재생성 API 응답 수신:', {
        success: response.data.success,
        hasAnswer: !!response.data.answer,
        answerLength: response.data.answer?.length || 0,
        answerPreview: response.data.answer?.substring(0, 150) || 'N/A',
        responseLanguage: response.data.language
      });

      if (response.data.success && response.data.answer) {
        // 3단계: 새 답변 저장
        currentAnswers[language] = response.data.answer;
        
        console.log('💾 새 답변 저장:', {
          faqId,
          language,
          newAnswerLength: response.data.answer.length,
          allAnswerKeys: Object.keys(currentAnswers)
        });

        await updateDoc(faqRef, {
          answer: currentAnswers,
          hasAnswer: true,
          updatedAt: serverTimestamp(),
          version: (faq.version || 1) + 1
        });

        console.log('✅ 답변 재생성 완료 - Firestore 업데이트됨');
        
        // 업데이트 반영 대기
        await new Promise(resolve => setTimeout(resolve, 500));
        
        showToastMessage(`${LANGUAGES.find(l => l.id === language)?.label} 답변이 재생성되었습니다.`, 'success');
        
        // 해당 FAQ만 업데이트 (전체 리로드 없이)
        await updateSingleFAQ(faqId, faq.category);
      } else {
        console.error('❌ 재생성 API 응답 실패:', {
          success: response.data.success,
          message: response.data.message
        });
        showToastMessage(response.data.message || '답변 재생성에 실패했습니다.', 'error');
      }
    } catch (error) {
      console.error('❌ 답변 재생성 실패:', {
        error: error,
        message: error.message,
        response: error.response?.data
      });
      
      let errorMessage = '답변 재생성 중 오류가 발생했습니다.';
      if (error.response) {
        errorMessage = error.response.data?.message || `서버 오류: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
      } else {
        errorMessage = error.message || '알 수 없는 오류가 발생했습니다.';
      }
      
      showToastMessage(errorMessage, 'error');
    } finally {
      setGeneratingAnswer(null);
    }
  };

  // 모든 언어 답변 재생성 (한국어, 미얀마어)
  const handleRegenerateAllAnswers = async (faq) => {
    const faqId = (faq.originalIds && faq.originalIds.length > 0) ? faq.originalIds[0] : faq.id;
    
    setGeneratingAnswer({ faqId: faq.id, language: 'all' });
    
    try {
      console.log('🔄 모든 언어 답변 재생성 시작:', {
        faqId,
        category: faq.category,
        hasKoQuestion: !!faq.question?.ko,
        hasMyQuestion: !!faq.question?.my
      });

      // 1단계: 재생성할 언어들의 기존 답변 삭제
      const faqRef = doc(db, `artifacts/${appId}/consultation_faqs/${faq.category}/faqs/${faqId}`);
      const currentAnswers = { ...(faq.answer || {}) };
      const languagesToRegenerate = [];

      // 재생성할 언어 확인 및 기존 답변 삭제
      for (const lang of LANGUAGES) {
        const question = faq.question?.[lang.id];
        if (question && question.trim()) {
          languagesToRegenerate.push(lang.id);
          // 해당 언어 답변 삭제
          delete currentAnswers[lang.id];
          console.log(`🗑️ ${lang.label} 기존 답변 삭제 예정`);
        }
      }

      // 기존 답변 삭제 후 업데이트
      if (languagesToRegenerate.length > 0) {
        console.log('🗑️ 기존 답변 삭제:', {
          faqId,
          deletedLanguages: languagesToRegenerate,
          remainingAnswers: Object.keys(currentAnswers)
        });

        await updateDoc(faqRef, {
          answer: currentAnswers,
          updatedAt: serverTimestamp()
        });

        console.log('✅ 기존 답변 삭제 완료');
      }

      // 2단계: 새 답변 생성
      let successCount = 0;
      let failCount = 0;
      const newAnswers = { ...currentAnswers }; // 삭제된 답변을 제외한 나머지 답변 유지

      // 미얀마어를 먼저 생성, 그 다음 한국어 생성
      const languagesInOrder = [...LANGUAGES].sort((a, b) => {
        // 미얀마어('my')를 먼저, 그 다음 한국어('ko')
        if (a.id === 'my') return -1;
        if (b.id === 'my') return 1;
        return 0;
      });

      for (const lang of languagesInOrder) {
        const question = faq.question?.[lang.id];
        if (!question || !question.trim()) {
          console.log(`⚠️ ${lang.label} 질문이 없어 건너뜀`);
          continue;
        }

        try {
          console.log(`📤 ${lang.label} 답변 생성 시작...`, {
            language: lang.id,
            questionPreview: question.substring(0, 50) + '...',
            questionLength: question.length
          });
          
          const response = await apiClient.post('/api/consultation/generate-answer', {
            question,
            category: faq.category,
            language: lang.id
          }, {
            timeout: 120000 // 2분
          });

          console.log(`📥 ${lang.label} API 응답:`, {
            success: response.data.success,
            hasAnswer: !!response.data.answer,
            answerLength: response.data.answer?.length || 0,
            responseLanguage: response.data.language
          });

          if (response.data.success && response.data.answer) {
            // 답변을 임시 객체에 저장 (나중에 한 번에 업데이트)
            newAnswers[lang.id] = response.data.answer;
            
            console.log(`✅ ${lang.label} 답변 생성 완료 (임시 저장)`, {
              language: lang.id,
              answerLength: response.data.answer.length,
              allAnswersSoFar: Object.keys(newAnswers)
            });
            
            successCount++;
            
            // API 호출 간 지연 (Rate Limit 방지)
            await new Promise(resolve => setTimeout(resolve, 2000));
          } else {
            console.error(`❌ ${lang.label} 답변 생성 실패:`, response.data.message);
            failCount++;
          }
        } catch (error) {
          console.error(`❌ ${lang.label} 답변 생성 오류:`, {
            error: error.message,
            response: error.response?.data,
            status: error.response?.status
          });
          failCount++;
        }
      }

      // 3단계: 모든 답변을 한 번에 Firestore에 업데이트
      if (successCount > 0) {
        console.log('💾 모든 답변을 Firestore에 업데이트:', {
          faqId,
          category: faq.category,
          newAnswers: Object.keys(newAnswers),
          successCount,
          failCount
        });

        await updateDoc(faqRef, {
          answer: newAnswers, // 모든 언어 답변을 한 번에 업데이트
          hasAnswer: true,
          updatedAt: serverTimestamp(),
          version: (faq.version || 1) + 1
        });

        console.log('✅ 모든 답변 업데이트 완료 - Firestore 반영됨');
        
        // 업데이트 반영 대기
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // 최종 결과
      if (successCount > 0) {
        const successLanguages = LANGUAGES.filter(lang => newAnswers[lang.id]).map(lang => lang.label).join(', ');
        showToastMessage(`답변 재생성 완료: ${successLanguages} (성공 ${successCount}개${failCount > 0 ? `, 실패 ${failCount}개` : ''})`, 'success');
        // 해당 FAQ만 업데이트 (전체 리로드 없이)
        await updateSingleFAQ(faqId, faq.category);
      } else {
        showToastMessage('답변 재생성에 실패했습니다.', 'error');
      }
    } catch (error) {
      console.error('답변 재생성 실패:', error);
      showToastMessage('답변 재생성 중 오류가 발생했습니다.', 'error');
    } finally {
      setGeneratingAnswer(null);
    }
  };

  // 모든 답변 생성 (미얀마어 우선, 그 다음 한국어) - 답변이 없는 경우용
  const handleGenerateAllAnswers = async (faq) => {
    setGeneratingAnswer({ faqId: faq.id, language: 'all' });
    try {
      // 미얀마어를 먼저 생성, 그 다음 한국어 생성
      const languagesInOrder = [...LANGUAGES].sort((a, b) => {
        // 미얀마어('my')를 먼저, 그 다음 한국어('ko')
        if (a.id === 'my') return -1;
        if (b.id === 'my') return 1;
        return 0;
      });
      
      for (const lang of languagesInOrder) {
        const question = faq.question?.[lang.id];
        if (question && question.trim()) {
          await handleGenerateAnswer(faq, lang.id);
          // API 호출 간 약간의 지연
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      showToastMessage('모든 답변이 생성되었습니다.', 'success');
      // handleGenerateAnswer가 이미 updateSingleFAQ를 호출하므로 여기서는 추가 작업 불필요
    } catch (error) {
      console.error('답변 생성 실패:', error);
      showToastMessage('답변 생성에 실패했습니다.', 'error');
    } finally {
      setGeneratingAnswer(null);
    }
  };

  // 답변이 있는지 확인
  const hasAnswer = (faq, language) => {
    const answer = faq.answer?.[language];
    return answer && answer.trim().length > 0;
  };

  // 질의 보완 (한국어/미얀마어 추가)
  const handleCompleteQuestion = async (faq) => {
    // 그룹핑된 FAQ의 경우 첫 번째 originalIds 사용
    const faqId = (faq.originalIds && faq.originalIds.length > 0) ? faq.originalIds[0] : faq.id;
    const displayFaqId = faq.id; // UI 업데이트용 (그룹핑된 FAQ의 경우)
    
    setCompletingQuestion({ faqId: displayFaqId });
    
    try {
      const question = faq.question || {};
      const hasKo = question.ko && question.ko.trim().length > 0;
      const hasMy = question.my && question.my.trim().length > 0;
      
      console.log('🔧 질의 보완 시작:', {
        faqId,
        displayFaqId,
        hasKo,
        hasMy,
        category: faq.category,
        originalIds: faq.originalIds,
        question: question
      });

      // 보완할 질의가 없는 경우
      if (hasKo && hasMy) {
        showToastMessage('보완할 질의가 없습니다. (한국어와 미얀마어가 모두 있습니다)', 'info');
        return;
      }

      if (!hasKo && !hasMy) {
        showToastMessage('번역할 질문이 없습니다. (한국어와 미얀마어 질문이 모두 없습니다)', 'error');
        return;
      }

      const faqRef = doc(db, `artifacts/${appId}/consultation_faqs/${faq.category}/faqs/${faqId}`);
      const updatedQuestion = { ...question };
      let needsUpdate = false;
      let keywordsUpdated = false;
      let translationError = null;

      // 케이스 1: 한국어가 없는 경우 → 한국어 추가
      if (!hasKo && hasMy) {
        console.log('   🔄 한국어 번역 추가 중...');
        console.log('   📝 원본 질문:', question.my.substring(0, 100));
        try {
          const response = await apiClient.post('/api/consultation/translate-question', {
            question: question.my,
            sourceLanguage: 'my',
            targetLanguage: 'ko',
            category: faq.category
          }, {
            timeout: 60000
          });

          console.log('   📥 번역 API 응답:', {
            success: response.data.success,
            hasTranslatedQuestion: !!response.data.translatedQuestion,
            translatedLength: response.data.translatedQuestion?.length || 0
          });

          if (response.data.success && response.data.translatedQuestion) {
            updatedQuestion.ko = response.data.translatedQuestion.trim();
            needsUpdate = true;
            console.log('   ✅ 한국어 번역 완료:', updatedQuestion.ko.substring(0, 50));
          } else {
            translationError = response.data.message || '번역 결과를 받을 수 없습니다.';
            console.error('   ❌ 번역 응답 실패:', response.data);
          }
        } catch (error) {
          translationError = error.response?.data?.message || error.message || '한국어 번역에 실패했습니다.';
          console.error('   ❌ 한국어 번역 실패:', {
            error: error.message,
            response: error.response?.data,
            status: error.response?.status
          });
        }
      }

      // 케이스 2: 미얀마어가 없는 경우 → 미얀마어 추가
      if (!hasMy && hasKo) {
        console.log('   🔄 미얀마어 번역 추가 중...');
        console.log('   📝 원본 질문:', question.ko.substring(0, 100));
        try {
          const response = await apiClient.post('/api/consultation/translate-question', {
            question: question.ko,
            sourceLanguage: 'ko',
            targetLanguage: 'my',
            category: faq.category
          }, {
            timeout: 60000
          });

          console.log('   📥 번역 API 응답:', {
            success: response.data.success,
            hasTranslatedQuestion: !!response.data.translatedQuestion,
            translatedLength: response.data.translatedQuestion?.length || 0
          });

          if (response.data.success && response.data.translatedQuestion) {
            updatedQuestion.my = response.data.translatedQuestion.trim();
            needsUpdate = true;
            console.log('   ✅ 미얀마어 번역 완료:', updatedQuestion.my.substring(0, 50));

            // 키워드도 생성
            if (!faq.keywords || faq.keywords.length === 0) {
              try {
                console.log('   🔑 키워드 생성 중...');
                const keywordsResponse = await apiClient.post('/api/consultation/generate-keywords', {
                  question: question.ko,
                  category: faq.category,
                  language: 'ko'
                }, {
                  timeout: 60000
                });

                if (keywordsResponse.data.success && keywordsResponse.data.keywords) {
                  await updateDoc(faqRef, {
                    keywords: keywordsResponse.data.keywords,
                    updatedAt: serverTimestamp()
                  });
                  keywordsUpdated = true;
                  console.log('   ✅ 키워드 생성 완료:', keywordsResponse.data.keywords);
                }
              } catch (error) {
                console.error('   ⚠️ 키워드 생성 실패:', error);
                // 키워드 생성 실패는 치명적이지 않으므로 계속 진행
              }
            }
          } else {
            translationError = response.data.message || '번역 결과를 받을 수 없습니다.';
            console.error('   ❌ 번역 응답 실패:', response.data);
          }
        } catch (error) {
          translationError = error.response?.data?.message || error.message || '미얀마어 번역에 실패했습니다.';
          console.error('   ❌ 미얀마어 번역 실패:', {
            error: error.message,
            response: error.response?.data,
            status: error.response?.status
          });
        }
      }

      // 번역 오류가 발생한 경우
      if (translationError) {
        showToastMessage(translationError, 'error');
        return;
      }

      // 업데이트가 필요한 경우 Firestore에 저장
      if (needsUpdate) {
        console.log('   💾 Firestore 업데이트 중...');
        console.log('   📝 업데이트할 질문:', updatedQuestion);
        
        await updateDoc(faqRef, {
          question: updatedQuestion,
          updatedAt: serverTimestamp()
        });

        console.log('✅ 질의 보완 완료 - Firestore 반영됨');
        showToastMessage(
          `질의가 보완되었습니다.${keywordsUpdated ? ' (키워드도 생성됨)' : ''}`,
          'success'
        );
        
        // 업데이트 반영 대기
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // 해당 FAQ만 업데이트 (전체 리로드 없이)
        // 그룹핑된 FAQ의 경우 displayFaqId로 찾아야 함
        await updateSingleFAQ(faqId, faq.category);
      } else {
        showToastMessage('번역할 질문이 없습니다.', 'warning');
      }
    } catch (error) {
      console.error('❌ 질의 보완 실패:', {
        error: error.message,
        stack: error.stack,
        response: error.response?.data,
        status: error.response?.status
      });
      
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          '질의 보완 중 오류가 발생했습니다.';
      showToastMessage(errorMessage, 'error');
    } finally {
      setCompletingQuestion(null);
    }
  };

  return (
    <div className="p-6 space-y-6" style={{ fontSize: '11px' }}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" style={{ fontSize: '16px' }}>
            <MessageSquare className="w-6 h-6" />
            전문가 상담 FAQ 관리
          </h1>
          <p className="text-gray-600 mt-1" style={{ fontSize: '11px' }}>
            자주 묻는 질문(FAQ)을 관리하고 사용자에게 빠른 답변을 제공하세요
          </p>
        </div>
        <button
          onClick={handleAddFAQ}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          style={{ fontSize: '11px' }}
        >
          <Plus className="w-4 h-4" />
          FAQ 추가
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600" style={{ fontSize: '10px' }}>총 FAQ 수</p>
              <p className="text-2xl font-bold text-gray-900 mt-1" style={{ fontSize: '18px' }}>
                {stats.total}
              </p>
            </div>
            <FileText className="w-8 h-8 text-indigo-600" />
          </div>
        </motion.div>

        {CATEGORIES.map(category => (
          <motion.div
            key={category.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`bg-white rounded-lg shadow p-4 border-l-4 border-${category.color}-500`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600" style={{ fontSize: '10px' }}>{category.label}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1" style={{ fontSize: '18px' }}>
                  {stats.byCategory[category.id] || 0}
                </p>
              </div>
              <TrendingUp className={`w-8 h-8 text-${category.color}-600`} />
            </div>
          </motion.div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-gray-700 mb-1" style={{ fontSize: '10px' }}>검색</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="질문, 답변, 키워드 검색..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                style={{ fontSize: '11px' }}
              />
            </div>
          </div>

          <div>
            <label className="block text-gray-700 mb-1" style={{ fontSize: '10px' }}>카테고리</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              style={{ fontSize: '11px' }}
            >
              <option value="all">전체</option>
              {CATEGORIES.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-gray-700 mb-1" style={{ fontSize: '10px' }}>언어</label>
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              style={{ fontSize: '11px' }}
            >
              <option value="all">전체</option>
              {LANGUAGES.map(lang => (
                <option key={lang.id} value={lang.id}>{lang.flag} {lang.label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={loadFAQs}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              style={{ fontSize: '11px' }}
            >
              <RefreshCw className="w-4 h-4" />
              새로고침
            </button>
          </div>
        </div>
      </div>

      {/* FAQ List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900" style={{ fontSize: '12px' }}>
            FAQ 목록 ({filteredFaqs.length}개)
          </h2>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" />
            <p className="text-gray-600 mt-2" style={{ fontSize: '11px' }}>로딩 중...</p>
          </div>
        ) : filteredFaqs.length === 0 ? (
          <div className="p-8 text-center">
            <MessageSquare className="w-12 h-12 mx-auto text-gray-400" />
            <p className="text-gray-600 mt-2" style={{ fontSize: '11px' }}>FAQ가 없습니다.</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-200">
              {paginatedFaqs.map((faq, index) => {
                const hasAnyAnswer = LANGUAGES.some(lang => hasAnswer(faq, lang.id));
                const hasNoAnswer = !hasAnyAnswer;
                return (
              <motion.div
                key={faq.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.02 }}
                className="p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span
                        className={`px-2 py-1 rounded text-white text-xs font-medium bg-${getCategoryColor(faq.category)}-500`}
                        style={{ fontSize: '10px' }}
                      >
                        {CATEGORIES.find(c => c.id === faq.category)?.label || faq.category}
                      </span>
                      {/* 답변 표시 배지 */}
                      {hasAnyAnswer ? (
                        <span className="px-2 py-1 rounded bg-green-100 text-green-700 text-xs flex items-center gap-1" style={{ fontSize: '10px' }}>
                          <CheckCircle className="w-3 h-3" />
                          답변 있음
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded bg-red-100 text-red-700 text-xs flex items-center gap-1" style={{ fontSize: '10px' }}>
                          <XCircle className="w-3 h-3" />
                          답변 없음
                        </span>
                      )}
                      {faq.priority && (
                        <span className="px-2 py-1 rounded bg-gray-100 text-gray-700 text-xs" style={{ fontSize: '10px' }}>
                          우선순위: {faq.priority}
                        </span>
                      )}
                      {faq.usageCount > 0 && (
                        <span className="px-2 py-1 rounded bg-blue-100 text-blue-700 text-xs" style={{ fontSize: '10px' }}>
                          사용: {faq.usageCount}회
                        </span>
                      )}
                    </div>

                    {/* Questions */}
                    <div className="space-y-1 mb-2">
                      {LANGUAGES.map(lang => {
                        const question = faq.question?.[lang.id];
                        const hasAnswerForLang = hasAnswer(faq, lang.id);
                        if (!question) return null;
                        return (
                          <div key={lang.id} className="flex items-start gap-2">
                            <span className="text-gray-500" style={{ fontSize: '10px' }}>{lang.flag}</span>
                            <div className="flex-1 flex items-center gap-2">
                              <p className="text-gray-900 font-medium flex-1" style={{ fontSize: '11px' }}>
                                {question}
                              </p>
                              {hasAnswerForLang && (
                                <CheckCircle className="w-3 h-3 text-green-600 flex-shrink-0" title="답변 있음" />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* 질의 보완 필요 여부 확인 */}
                    {(() => {
                      const question = faq.question || {};
                      const hasKo = question.ko && question.ko.trim().length > 0;
                      const hasMy = question.my && question.my.trim().length > 0;
                      const needsCompletion = (!hasKo && hasMy) || (hasKo && !hasMy);
                      return needsCompletion ? (
                        <div className="mb-2">
                          <button
                            onClick={() => handleCompleteQuestion(faq)}
                            disabled={completingQuestion?.faqId === faq.id}
                            className="px-3 py-1.5 bg-amber-600 text-white rounded text-xs hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                            style={{ fontSize: '11px' }}
                          >
                            {completingQuestion?.faqId === faq.id ? (
                              <>
                                <RefreshCw className="w-3 h-3 animate-spin" />
                                질의 보완 중...
                              </>
                            ) : (
                              <>
                                <Languages className="w-3 h-3" />
                                질의 보완
                              </>
                            )}
                          </button>
                        </div>
                      ) : null;
                    })()}

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 mb-2">
                      {hasNoAnswer ? (
                        <button
                          onClick={() => {
                            // 한국어와 미얀마어 답변 모두 생성
                            handleGenerateAllAnswers(faq);
                          }}
                          disabled={generatingAnswer?.faqId === faq.id}
                          className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                          style={{ fontSize: '11px' }}
                        >
                          {generatingAnswer?.faqId === faq.id ? (
                            <>
                              <RefreshCw className="w-3 h-3 animate-spin" />
                              답변 생성 중...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-3 h-3" />
                              답변 생성
                            </>
                          )}
                        </button>
                      ) : (
                        <button
                          onClick={() => toggleExpand(faq.id)}
                          className="px-3 py-1.5 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 transition-colors flex items-center gap-1"
                          style={{ fontSize: '11px' }}
                        >
                          {expandedFaqs.has(faq.id) ? (
                            <>
                              <ChevronUp className="w-3 h-3" />
                              답변 숨기기
                            </>
                          ) : (
                            <>
                              <Eye className="w-3 h-3" />
                              답변 보기
                            </>
                          )}
                        </button>
                      )}
                    </div>

                    <AnimatePresence>
                      {expandedFaqs.has(faq.id) && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-2 p-3 bg-gray-50 rounded-lg"
                        >
                          {/* 모든 언어 답변 재생성 버튼 (답변이 있는 경우) */}
                          {hasAnyAnswer && (
                            <div className="mb-3 pb-3 border-b border-gray-300">
                              <button
                                onClick={async () => {
                                  if (!window.confirm('한국어와 미얀마어 답변을 모두 재생성하시겠습니까?')) {
                                    return;
                                  }
                                  await handleRegenerateAllAnswers(faq);
                                }}
                                disabled={generatingAnswer?.faqId === faq.id && generatingAnswer?.language === 'all'}
                                className="w-full px-3 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg text-xs hover:from-purple-700 hover:to-indigo-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
                                style={{ fontSize: '11px' }}
                              >
                                {generatingAnswer?.faqId === faq.id && generatingAnswer?.language === 'all' ? (
                                  <>
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                    모든 언어 답변 재생성 중...
                                  </>
                                ) : (
                                  <>
                                    <RefreshCw className="w-4 h-4" />
                                    <span>한국어 + 미얀마어 답변 재생성</span>
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                          {LANGUAGES.map(lang => {
                            const answer = faq.answer?.[lang.id];
                            const hasAnswerForLang = hasAnswer(faq, lang.id);
                            const isGenerating = generatingAnswer?.faqId === faq.id && generatingAnswer?.language === lang.id;
                            
                            return (
                              <div key={lang.id} className="mb-3 last:mb-0">
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-2">
                                    <Languages className="w-3 h-3 text-gray-500" />
                                    <span className="text-gray-700 font-medium" style={{ fontSize: '10px' }}>
                                      {lang.flag} {lang.label}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {!hasAnswerForLang ? (
                                      <button
                                        onClick={() => handleGenerateAnswer(faq, lang.id)}
                                        disabled={isGenerating || !faq.question?.[lang.id]}
                                        className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                                        style={{ fontSize: '10px' }}
                                      >
                                        {isGenerating ? (
                                          <>
                                            <RefreshCw className="w-3 h-3 animate-spin" />
                                            생성 중...
                                          </>
                                        ) : (
                                          <>
                                            <Sparkles className="w-3 h-3" />
                                            답변 생성
                                          </>
                                        )}
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => handleRegenerateAnswer(faq, lang.id)}
                                        disabled={isGenerating}
                                        className="px-2 py-1 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                                        style={{ fontSize: '10px' }}
                                      >
                                        {isGenerating ? (
                                          <>
                                            <RefreshCw className="w-3 h-3 animate-spin" />
                                            재생성 중...
                                          </>
                                        ) : (
                                          <>
                                            <RefreshCw className="w-3 h-3" />
                                            답변 재생성
                                          </>
                                        )}
                                      </button>
                                    )}
                                  </div>
                                </div>
                                {hasAnswerForLang ? (
                                  <p className="text-gray-700 whitespace-pre-wrap" style={{ fontSize: '11px', lineHeight: '1.6' }}>
                                    {answer}
                                  </p>
                                ) : (
                                  <p className="text-gray-400 italic" style={{ fontSize: '10px' }}>
                                    답변이 없습니다. "답변 생성" 버튼을 클릭하여 AI로 답변을 생성하세요.
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Keywords */}
                    {faq.keywords && faq.keywords.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {faq.keywords.slice(0, 5).map((keyword, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 rounded bg-gray-100 text-gray-600 text-xs"
                            style={{ fontSize: '9px' }}
                          >
                            {keyword}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => handleEditFAQ(faq)}
                      className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                      title="수정"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteFAQ(faq)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="삭제"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
              );
            })}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="p-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-gray-600" style={{ fontSize: '10px' }}>
                  {startIndex + 1} - {Math.min(endIndex, filteredFaqs.length)} / {filteredFaqs.length}개
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ fontSize: '11px' }}
                  >
                    이전
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-2 py-1 rounded ${
                            currentPage === pageNum
                              ? 'bg-indigo-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          style={{ fontSize: '11px' }}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ fontSize: '11px' }}
                  >
                    다음
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* User Query Statistics */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-gray-900 flex items-center gap-2" style={{ fontSize: '12px' }}>
              <Activity className="w-4 h-4" />
              사용자 질의 통계
            </h2>
            <p className="text-gray-600 mt-1" style={{ fontSize: '10px' }}>
              사용자들의 상담 질문 패턴을 분석하세요
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={statsPeriod}
              onChange={(e) => setStatsPeriod(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              style={{ fontSize: '10px' }}
            >
              <option value="7">최근 7일</option>
              <option value="30">최근 30일</option>
              <option value="90">최근 90일</option>
            </select>
            <button
              onClick={loadQueryStatistics}
              disabled={loadingQueryStats}
              className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loadingQueryStats ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {loadingQueryStats ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" />
            <p className="text-gray-600 mt-2" style={{ fontSize: '11px' }}>통계 로딩 중...</p>
          </div>
        ) : (
          <div className="p-4 space-y-4">
            {/* 주요 통계 카드 */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>총 질의 수</p>
                    <p className="text-2xl font-bold text-blue-900 mt-1" style={{ fontSize: '18px' }}>
                      {queryStats.total}
                    </p>
                  </div>
                  <Users className="w-8 h-8 text-blue-600" />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>FAQ 매칭률</p>
                    <p className="text-2xl font-bold text-green-900 mt-1" style={{ fontSize: '18px' }}>
                      {queryStats.faqMatchRate}%
                    </p>
                  </div>
                  <Target className="w-8 h-8 text-green-600" />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4 border border-indigo-200"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>FAQ 매칭</p>
                    <p className="text-2xl font-bold text-indigo-900 mt-1" style={{ fontSize: '18px' }}>
                      {queryStats.answeredCount}
                    </p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-indigo-600" />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>미매칭 질문</p>
                    <p className="text-2xl font-bold text-red-900 mt-1" style={{ fontSize: '18px' }}>
                      {queryStats.unansweredCount}
                    </p>
                  </div>
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
              </motion.div>
            </div>

            {/* 카테고리별 통계 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3" style={{ fontSize: '11px' }}>카테고리별 질의 수</h3>
                <div className="space-y-2">
                  {CATEGORIES.map(cat => (
                    <div key={cat.id} className="flex items-center justify-between">
                      <span className="text-gray-700" style={{ fontSize: '10px' }}>{cat.label}</span>
                      <span className="font-semibold text-gray-900" style={{ fontSize: '11px' }}>
                        {queryStats.byCategory[cat.id] || 0}건
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3" style={{ fontSize: '11px' }}>언어별 질의 수</h3>
                <div className="space-y-2">
                  {LANGUAGES.map(lang => (
                    <div key={lang.id} className="flex items-center justify-between">
                      <span className="text-gray-700" style={{ fontSize: '10px' }}>
                        {lang.flag} {lang.label}
                      </span>
                      <span className="font-semibold text-gray-900" style={{ fontSize: '11px' }}>
                        {queryStats.byLanguage[lang.id] || 0}건
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 자주 묻는 질문 */}
            {queryStats.topQuestions && queryStats.topQuestions.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2" style={{ fontSize: '11px' }}>
                  <TrendingUp className="w-4 h-4" />
                  자주 묻는 질문 (상위 {queryStats.topQuestions.length}개)
                </h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {queryStats.topQuestions.map((item, idx) => (
                    <div key={idx} className="flex items-start justify-between gap-2 p-2 bg-white rounded border border-gray-200">
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-900 font-medium mb-1" style={{ fontSize: '11px' }}>
                          {item.question}
                        </p>
                        <p className="text-gray-500" style={{ fontSize: '10px' }}>
                          {item.count}회 질문
                        </p>
                      </div>
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold flex-shrink-0" style={{ fontSize: '9px' }}>
                        #{idx + 1}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900" style={{ fontSize: '14px' }}>
                  {isEditMode ? 'FAQ 수정' : 'FAQ 추가'}
                </h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {/* Category */}
                <div>
                  <label className="block text-gray-700 mb-2" style={{ fontSize: '10px' }}>카테고리 *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    style={{ fontSize: '11px' }}
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                {/* Questions */}
                <div>
                  <label className="block text-gray-700 mb-2" style={{ fontSize: '10px' }}>질문 *</label>
                  <div className="space-y-2">
                    {LANGUAGES.map(lang => (
                      <div key={lang.id}>
                        <label className="block text-gray-600 mb-1" style={{ fontSize: '10px' }}>
                          {lang.flag} {lang.label}
                        </label>
                        <textarea
                          value={formData.question[lang.id] || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            question: { ...formData.question, [lang.id]: e.target.value }
                          })}
                          placeholder={`${lang.label} 질문을 입력하세요...`}
                          rows={2}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 resize-none"
                          style={{ fontSize: '11px' }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Answers */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-gray-700" style={{ fontSize: '10px' }}>답변 *</label>
                    <button
                      type="button"
                      onClick={async () => {
                        // 모든 언어의 답변을 한번에 생성
                        const languagesToGenerate = LANGUAGES.filter(lang => {
                          const question = formData.question[lang.id];
                          return question && question.trim().length > 0;
                        });

                        if (languagesToGenerate.length === 0) {
                          showToastMessage('최소한 한 가지 언어의 질문을 입력해주세요.', 'error');
                          return;
                        }

                        setLoading(true);
                        try {
                          const newAnswers = { ...formData.answer };
                          
                          for (const lang of languagesToGenerate) {
                            try {
                              const response = await apiClient.post('/api/consultation/generate-answer', {
                                question: formData.question[lang.id],
                                category: formData.category,
                                language: lang.id
                              });

                              if (response.data.success) {
                                newAnswers[lang.id] = response.data.answer;
                                showToastMessage(`${lang.label} 답변 생성 완료`, 'success');
                              }
                            } catch (error) {
                              console.error(`${lang.label} 답변 생성 실패:`, error);
                              showToastMessage(`${lang.label} 답변 생성 실패`, 'error');
                            }
                          }

                          setFormData({ ...formData, answer: newAnswers });
                        } catch (error) {
                          console.error('답변 생성 실패:', error);
                          showToastMessage('답변 생성 중 오류가 발생했습니다.', 'error');
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                      style={{ fontSize: '10px' }}
                    >
                      <Sparkles className="w-3 h-3" />
                      AI 답변 자동 생성
                    </button>
                  </div>
                  <div className="space-y-2">
                    {LANGUAGES.map(lang => (
                      <div key={lang.id}>
                        <label className="block text-gray-600 mb-1" style={{ fontSize: '10px' }}>
                          {lang.flag} {lang.label}
                        </label>
                        <textarea
                          value={formData.answer[lang.id] || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            answer: { ...formData.answer, [lang.id]: e.target.value }
                          })}
                          placeholder={`${lang.label} 답변을 입력하거나 "AI 답변 자동 생성" 버튼을 클릭하세요...`}
                          rows={6}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 resize-none"
                          style={{ fontSize: '11px' }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Priority & Keywords */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 mb-2" style={{ fontSize: '10px' }}>우선순위 (1-10)</label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 5 })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      style={{ fontSize: '11px' }}
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-2" style={{ fontSize: '10px' }}>키워드 (쉼표로 구분)</label>
                    <input
                      type="text"
                      value={formData.keywords.join(', ')}
                      onChange={(e) => setFormData({
                        ...formData,
                        keywords: e.target.value.split(',').map(k => k.trim()).filter(k => k)
                      })}
                      placeholder="키워드1, 키워드2, ..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      style={{ fontSize: '11px' }}
                    />
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  style={{ fontSize: '11px' }}
                >
                  취소
                </button>
                <button
                  onClick={handleSaveFAQ}
                  disabled={loading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  style={{ fontSize: '11px' }}
                >
                  <Save className="w-4 h-4" />
                  저장
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ConsultationFAQManagementPage;

